import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clientService } from "../../services/clients";
import Button from "../common/Button";
import Modal from "../common/Modal";
import ClientForm from "./ClientForm";
import toast from "react-hot-toast";
import { format } from "date-fns";

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: client, isLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: () => clientService.getById(id),
  });

  const deleteClient = useMutation({
    mutationFn: () => clientService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Client deleted");
      navigate("/clients");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error deleting client");
    },
  });

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      deleteClient.mutate();
    }
  };

  if (isLoading) return <div>Loading...</div>;

  const formatDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "dd MMM yyyy");
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{client.name}</h1>
        <div className="space-x-2">
          <Button variant="secondary" onClick={() => navigate("/clients")}>
            Back
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>Edit</Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </div>
      <div className="bg-white shadow rounded-lg p-6 space-y-3">
        <p><span className="font-medium">Email:</span> {client.email}</p>
        <p><span className="font-medium">Phone:</span> {client.phone}</p>
        <p><span className="font-medium">Company:</span> {client.company || "-"}</p>
        <p><span className="font-medium">Business Type:</span> {client.businessType || "-"}</p>
        <p><span className="font-medium">Services Availed:</span> {client.servicesAvailed?.join(", ") || "-"}</p>
        <p><span className="font-medium">Contract Start:</span> {formatDate(client.contractStart)}</p>
        <p><span className="font-medium">Contract End:</span> {formatDate(client.contractEnd)}</p>
        <p><span className="font-medium">Contract Value:</span> {client.contractValue != null ? `₹${client.contractValue}` : "-"}</p>
        <p><span className="font-medium">Payment Status:</span> {client.paymentStatus || "-"}</p>
        <p><span className="font-medium">Health Score:</span> {client.healthScore != null ? client.healthScore : "-"}</p>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Edit Client"
      >
        <ClientForm
          client={client}
          onSuccess={() => {
            setIsModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["client", id] });
          }}
        />
      </Modal>
    </div>
  );
};

export default ClientDetails;
